import React, { useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

interface AccordionItemProps {
  title: string;
  description?: string;
  defaultOpen?: boolean;
  /** Controlled: when provided, open state is controlled by parent */
  open?: boolean;
  /** Controlled: called when user toggles; use with open for controlled mode */
  onToggle?: (open: boolean) => void;
  /** Optional key for parent to track which section is open (e.g. for Accordion openKeys) */
  itemKey?: string;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  description,
  defaultOpen = false,
  open: controlledOpen,
  onToggle,
  itemKey: _itemKey,
  children,
  className,
  headerClassName,
}) => {
  const { colors } = useTheme();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined && onToggle !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) onToggle(next);
      else setUncontrolledOpen(next);
    },
    [isControlled, onToggle],
  );

  return (
    <div className={cn('border rounded-lg overflow-hidden', colors.border, className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
          colors.text,
          colors.interactiveHover,
          headerClassName,
        )}
      >
        <div className="flex-1 min-w-0">
          <span className="font-medium">{title}</span>
          {description && (
            <p className={cn('text-xs mt-0.5 truncate', colors.textMuted)}>{description}</p>
          )}
        </div>
        <svg
          className={cn('w-5 h-5 flex-shrink-0 transition-transform', colors.textMuted, open && 'rotate-180')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className={cn('px-4 pb-4 pt-1 border-t', colors.border, colors.surface)}>
          {children}
        </div>
      )}
    </div>
  );
};

interface AccordionProps {
  children: React.ReactNode;
  className?: string;
  /** Optional: when set, open state is controlled; keys of open sections */
  openKeys?: string[];
  /** Optional: when set, called when a section is toggled; use with openKeys */
  onOpenChange?: (key: string, open: boolean) => void;
}

export const Accordion: React.FC<AccordionProps> = ({ children, className }) => {
  return <div className={cn('space-y-2', className)}>{children}</div>;
};
